// Cozy Cat Booth (issue 372): the camera, four snaps with a countdown and a flash, the look
// and stickers live in the viewfinder, then the strip prints and can be saved or shared. All of
// it happens on the customer's phone: the camera's pictures are drawn on canvases here, and the
// strip is a file made here. Nothing is sent anywhere, unless the customer pins the strip to the
// Cat Wall (issue 373): then that one strip (a JPEG), the cat's name and their consent go to CCK
// Ops, where Aditya approves it before it shows. The camera stops after the fourth snap, and
// whenever the booth is closed.
// Issue 380: the ears and whiskers follow faces. On Step inside, Google's MediaPipe face detector
// (served by CCK Ops: the section's face_kit address) loads in the background and finds faces in
// the camera, on the phone; until it's ready, or if it can't load, the stickers sit where the
// hint says.

import {
  FACE_STICKER_OPACITY,
  INK,
  PAPER,
  applyLook,
  ART,
  coverRect,
  earPlacements,
  facePose,
  fixedPose,
  pawPlacements,
  smoothPoses,
  whiskerPlacement,
  stripDate,
  stripFileName,
  stripLayout,
} from "@cck/booth-core";

const dialog = document.querySelector("[data-cck-booth]");

/** Where a pinned strip goes (CCK Ops), once the wall has said; empty: pinning isn't offered. */
let pinUrl = "";

const TAPES = [
  "var(--color-lilac)",
  "var(--color-pistachio)",
  "var(--color-pink)",
  "var(--color-peach)",
];
const TILTS = [-4, 3, -2, 5, -5, 2];

/** The Cat Wall on the booth's page (issue 373): approved strips, from CCK Ops. */
async function loadWall() {
  const wall = document.querySelector("[data-cck-wall]");
  if (!(wall instanceof HTMLElement)) return;
  const empty = (/** @type {string} */ text) => {
    const p = document.createElement("p");
    p.className = "cck-booth-wall__empty";
    p.textContent = text;
    wall.replaceChildren(p);
  };
  try {
    const res = await fetch(wall.dataset.endpoint ?? "", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data.pins)) throw new Error("no wall");
    pinUrl = typeof data.pinUrl === "string" ? data.pinUrl : "";
    if (!data.pins.length) return empty("No strips up yet. Be the first: step inside!");
    wall.replaceChildren(
      ...data.pins.map(
        (/** @type {{ name: string, url: string }} */ pin, /** @type {number} */ i) => {
          const fig = document.createElement("figure");
          fig.className = "cck-wall-pin";
          fig.style.setProperty("--tilt", `${TILTS[i % TILTS.length]}deg`);
          fig.style.setProperty("--tape", TAPES[i % TAPES.length] ?? "");
          const img = Object.assign(document.createElement("img"), {
            src: pin.url,
            alt: `${pin.name}'s Cozy Cat Booth strip`,
            loading: "lazy",
            width: 600,
            height: 1786,
          });
          const cap = document.createElement("figcaption");
          cap.textContent = pin.name;
          fig.append(img, cap);
          return fig;
        },
      ),
    );
  } catch {
    empty("The wall's having a nap. Come back soon.");
  }
}
loadWall();

if (dialog instanceof HTMLDialogElement) {
  /** Tests make the booth quicker: a countdown tick and the printing, in ms. */
  const fast = /** @type {any} */ (window);
  const TICK = Number(fast.CCK_BOOTH_TICK ?? 1000);
  const PRINT_MS = Number(fast.CCK_BOOTH_PRINT_MS ?? 2600);
  const SNAPS = 4;
  const PIN_WIDTH = 300;
  const ORANGE = "#F58D4E";
  const PINK = "#F8BCCA";
  const TUFT = "#FFFDF7";
  const PATHS = {
    ear: new Path2D(ART.ear),
    earInner: new Path2D(ART.earInner),
    earTufts: new Path2D(ART.earTufts),
    whiskers: new Path2D(ART.whiskers),
    nose: new Path2D(ART.nose),
    mouth: new Path2D(ART.mouth),
    pawPad: new Path2D(ART.pawPad),
  };
  const ink = `rgb(${INK.join(",")})`;
  const paper = `rgb(${PAPER.join(",")})`;

  const $ = (/** @type {string} */ sel) => /** @type {HTMLElement} */ (dialog.querySelector(sel));
  const view = /** @type {HTMLCanvasElement} */ ($("[data-cck-booth-view]"));
  const label = $("[data-cck-booth-label]");
  const dots = [...$("[data-cck-booth-dots]").children];
  const countdown = $("[data-cck-booth-countdown]");
  const flash = $("[data-cck-booth-flash]");
  const hint = $("[data-cck-booth-hint]");
  const shutter = /** @type {HTMLButtonElement} */ ($("[data-cck-booth-shutter]"));
  const chips = /** @type {HTMLButtonElement[]} */ ([
    ...dialog.querySelectorAll(".cck-booth__chip"),
  ]);
  const printing = /** @type {HTMLImageElement} */ ($("[data-cck-booth-printing]"));
  const stripImg = /** @type {HTMLImageElement} */ ($("[data-cck-booth-strip]"));
  const save = /** @type {HTMLAnchorElement} */ ($("[data-cck-booth-save]"));
  const share = /** @type {HTMLButtonElement} */ ($("[data-cck-booth-share]"));
  const pinForm = /** @type {HTMLFormElement} */ ($("[data-cck-booth-pin]"));
  const pinStatus = $("[data-cck-booth-pin-status]");
  const pinned = $("[data-cck-booth-pinned]");

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");

  /** @type {import("./cck-booth-core.js").Look} */
  let look = "sketch";
  /** @type {Set<import("./cck-booth-core.js").Sticker>} */
  const stickers = new Set(["ears"]);
  /** @type {MediaStream | null} */
  let stream = null;
  let frame = 0;
  /** The face kit (issue 380): "off" until Step inside, then "loading", "ready" or "failed". */
  let faceKit = "off";
  /** @type {{ detectForVideo(video: HTMLVideoElement, at: number): { detections: any[] } } | null} */
  let detector = null;
  /** The faces found in the camera's last frame (its own 0–1 picture). */
  /** @type {import("./cck-booth-core.js").Face[]} */
  let faces = [];
  /** Their eased poses on the viewfinder: what's drawn, and what the snaps use. */
  /** @type {import("./cck-booth-core.js").Pose[]} */
  let viewPoses = [];
  let lastFrameTime = -1;
  /** Bumped whenever the booth closes or starts over, so a run in progress stops. */
  let session = 0;
  let running = false;
  /** @type {HTMLCanvasElement[]} */
  let shots = [];
  let stripUrl = "";
  /** @type {File | null} */
  let stripFile = null;
  /** The strip as a JPEG, smaller, for pinning (issue 373). */
  /** @type {Blob | null} */
  let stripJpeg = null;

  const sleep = (/** @type {number} */ ms) => new Promise((done) => setTimeout(done, ms));

  /** @param {"snap" | "print" | "keep" | "trouble"} name */
  function show(name) {
    for (const panel of dialog.querySelectorAll("[data-cck-booth-panel]"))
      /** @type {HTMLElement} */ (panel).hidden =
        panel.getAttribute("data-cck-booth-panel") !== name;
  }

  /** @param {"denied" | "nocam"} why */
  function trouble(why) {
    stopCamera();
    $("[data-cck-booth-trouble-title]").textContent =
      why === "denied" ? "The booth needs your camera" : "I can't find a camera";
    $("[data-cck-booth-trouble-text]").textContent =
      why === "denied"
        ? "Your browser said no to the camera. Allow it for this site in your browser's settings, then try again."
        : "This device doesn't seem to have a camera I can use. Try the booth on your phone.";
    show("trouble");
  }

  async function startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) return trouble("nocam");
    const mine = session;
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      if (mine !== session || !dialog.open) {
        for (const t of s.getTracks()) t.stop();
        return;
      }
      stream = s;
      video.srcObject = s;
      await video.play();
      loop();
    } catch (err) {
      const name = /** @type {Error} */ (err).name;
      trouble(name === "NotAllowedError" || name === "SecurityError" ? "denied" : "nocam");
    }
  }

  function stopCamera() {
    cancelAnimationFrame(frame);
    for (const t of stream?.getTracks() ?? []) t.stop();
    stream = null;
    video.srcObject = null;
  }

  /**
   * Loads the face detector once, in the background (issue 380). Tests stand in for it with
   * window.CCK_BOOTH_FACES(), which returns faces as the detector would.
   */
  async function loadFaceKit() {
    if (faceKit !== "off") return;
    if (typeof fast.CCK_BOOTH_FACES === "function") {
      faceKit = "ready";
      dialog.dataset.faces = faceKit;
      return;
    }
    const base = dialog.dataset.faceKit;
    faceKit = base ? "loading" : "failed";
    dialog.dataset.faces = faceKit;
    if (!base) return;
    try {
      const vision = await import(`${base}/vision_bundle.mjs`);
      const files = await vision.FilesetResolver.forVisionTasks(`${base}/wasm`);
      const options = (/** @type {"GPU" | "CPU"} */ delegate) => ({
        baseOptions: { modelAssetPath: `${base}/blaze_face_short_range.tflite`, delegate },
        runningMode: "VIDEO",
        minDetectionConfidence: 0.5,
      });
      try {
        detector = await vision.FaceDetector.createFromOptions(files, options("GPU"));
      } catch {
        detector = await vision.FaceDetector.createFromOptions(files, options("CPU"));
      }
      faceKit = "ready";
    } catch {
      faceKit = "failed";
    }
    dialog.dataset.faces = faceKit;
  }

  /** The faces in the camera now: up to three, each its eyes, nose and width. */
  function detectFaces() {
    if (typeof fast.CCK_BOOTH_FACES === "function") return fast.CCK_BOOTH_FACES();
    if (faceKit !== "ready" || !detector) return [];
    if (video.currentTime === lastFrameTime) return faces;
    lastFrameTime = video.currentTime;
    try {
      return detector
        .detectForVideo(video, performance.now())
        .detections.filter((/** @type {any} */ d) => d.keypoints?.length >= 3 && d.boundingBox)
        .slice(0, 3)
        .map((/** @type {any} */ d) => ({
          eyes: [d.keypoints[0], d.keypoints[1]],
          nose: d.keypoints[2],
          width: d.boundingBox.width / video.videoWidth,
        }));
    } catch {
      faceKit = "failed";
      dialog.dataset.faces = faceKit;
      return [];
    }
  }

  /** What the hint says: the kit loading, come closer, or line up under the ears. */
  function setHint() {
    const text =
      faceKit === "loading"
        ? "getting the ears ready…"
        : faceKit === "ready"
          ? faces.length
            ? ""
            : "come a little closer"
          : "line your faces up under the ears";
    if (hint.textContent !== text) hint.textContent = text;
    hint.hidden = running || !text;
  }

  /**
   * The camera, mirrored like a mirror, cut to the canvas, in the look, with the stickers: on the
   * faces when the kit is ready (`poses`, in this canvas's pixels), or where the hint says.
   * @param {HTMLCanvasElement} canvas @param {import("./cck-booth-core.js").Pose[] | null} poses
   */
  function render(canvas, poses) {
    const { width: w, height: h } = canvas;
    const ctx = /** @type {CanvasRenderingContext2D} */ (
      canvas.getContext("2d", { willReadFrequently: true })
    );
    const r = coverRect(video.videoWidth, video.videoHeight, w, h);
    ctx.save();
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, w, h);
    ctx.restore();
    const img = ctx.getImageData(0, 0, w, h);
    applyLook(look, img.data, w, h);
    ctx.putImageData(img, 0, 0);
    drawStickers(ctx, w, h, poses ?? [fixedPose(w, h)]);
  }

  /** Poses on the viewfinder, for a canvas of another width (a snap). */
  const scalePoses = (
    /** @type {import("./cck-booth-core.js").Pose[]} */ poses,
    /** @type {number} */ k,
  ) =>
    poses.map((p) => ({
      x: p.x * k,
      y: p.y * k,
      angle: p.angle,
      size: p.size * k,
      nose: { x: p.nose.x * k, y: p.nose.y * k },
    }));

  /** Follows faces when the kit is ready; otherwise null (the stickers stay put). */
  const following = () => faceKit === "ready";

  /** The ears' and whiskers' own layer (issue 451), reused frame to frame. */
  const faceLayer = document.createElement("canvas");

  /**
   * @param {CanvasRenderingContext2D} canvasCtx @param {number} w @param {number} h
   * @param {import("./cck-booth-core.js").Pose[]} poses
   */
  function drawStickers(canvasCtx, w, h, poses) {
    // Issue 451: the ears and whiskers go on a layer of their own, put down at 80% opacity in one
    // go, so their overlapping fills and strokes don't show through each other.
    if (faceLayer.width !== w || faceLayer.height !== h) {
      faceLayer.width = w;
      faceLayer.height = h;
    }
    const layer = /** @type {CanvasRenderingContext2D} */ (faceLayer.getContext("2d"));
    layer.clearRect(0, 0, w, h);
    let ctx = layer;
    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    /** @param {{ x: number, y: number, angle: number, scale: number }} at @param {() => void} draw */
    const placed = (at, draw, mirror = false) => {
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.rotate(at.angle);
      ctx.scale(mirror ? -at.scale : at.scale, at.scale);
      draw();
      ctx.restore();
    };
    for (const pose of poses) {
      if (stickers.has("whiskers"))
        placed(whiskerPlacement(pose), () => {
          ctx.globalAlpha = 0.7;
          ctx.fillStyle = PINK;
          for (const [cx, cy, rx, ry] of ART.cheeks) {
            ctx.beginPath();
            ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
          ctx.strokeStyle = ink;
          ctx.lineWidth = 2.2;
          ctx.stroke(PATHS.whiskers);
          ctx.fillStyle = PINK;
          ctx.fill(PATHS.nose);
          ctx.lineWidth = 2.4;
          ctx.stroke(PATHS.nose);
          ctx.lineWidth = 2;
          ctx.stroke(PATHS.mouth);
        });
      if (stickers.has("ears"))
        for (const ear of earPlacements(pose))
          placed(
            ear,
            () => {
              ctx.translate(-50, -98);
              ctx.fillStyle = ORANGE;
              ctx.fill(PATHS.ear);
              ctx.strokeStyle = ink;
              ctx.lineWidth = 4;
              ctx.stroke(PATHS.ear);
              ctx.fillStyle = PINK;
              ctx.fill(PATHS.earInner);
              ctx.strokeStyle = TUFT;
              ctx.lineWidth = 3.2;
              ctx.stroke(PATHS.earTufts);
            },
            ear.mirror,
          );
    }
    ctx.restore();
    canvasCtx.save();
    canvasCtx.globalAlpha = FACE_STICKER_OPACITY;
    canvasCtx.drawImage(faceLayer, 0, 0);
    canvasCtx.restore();
    ctx = canvasCtx;
    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    if (stickers.has("paws"))
      for (const paw of pawPlacements(w, h))
        placed(paw, () => {
          ctx.fillStyle = PINK;
          ctx.strokeStyle = ink;
          ctx.lineWidth = 3.5;
          ctx.fill(PATHS.pawPad);
          ctx.stroke(PATHS.pawPad);
          for (const [cx, cy, rx, ry, turn] of ART.pawToes) {
            ctx.beginPath();
            ctx.ellipse(cx, cy, rx, ry, (turn * Math.PI) / 180, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
        });
    ctx.restore();
  }

  function loop() {
    if (video.videoWidth) {
      if (following()) {
        faces = detectFaces();
        viewPoses = smoothPoses(
          viewPoses,
          faces.map((f) =>
            facePose(f, video.videoWidth, video.videoHeight, view.width, view.height),
          ),
        );
      }
      render(view, following() ? viewPoses : null);
      if (!running) setHint();
    }
    frame = requestAnimationFrame(loop);
  }

  function setDots(/** @type {number} */ done) {
    dots.forEach((d, i) => d.classList.toggle("is-done", i < done));
  }

  function lockControls(/** @type {boolean} */ locked) {
    shutter.disabled = locked;
    for (const c of chips) c.disabled = locked;
  }

  async function run() {
    if (running || !stream) return;
    running = true;
    const mine = session;
    lockControls(true);
    hint.hidden = true;
    shots = [];
    for (let i = 0; i < SNAPS; i++) {
      label.textContent = `snap ${i + 1} of ${SNAPS}`;
      countdown.hidden = false;
      for (let n = 3; n > 0; n--) {
        countdown.textContent = String(n);
        await sleep(TICK);
        if (mine !== session) return;
      }
      countdown.hidden = true;
      const shot = document.createElement("canvas");
      shot.width = 640;
      shot.height = 480;
      render(shot, following() ? scalePoses(viewPoses, shot.width / view.width) : null);
      shots.push(shot);
      flash.classList.remove("is-on");
      void flash.offsetWidth; // restart the flash
      flash.classList.add("is-on");
      setDots(i + 1);
    }
    stopCamera();
    running = false;
    await printStrip(mine);
  }

  /** Lays the four shots on paper with the booth's footer, then prints it out of the slot. */
  async function printStrip(/** @type {number} */ mine) {
    const L = stripLayout(SNAPS, 520);
    const c = document.createElement("canvas");
    c.width = L.width;
    c.height = L.height;
    const ctx = /** @type {CanvasRenderingContext2D} */ (c.getContext("2d"));
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, L.width, L.height);
    ctx.strokeStyle = ink;
    ctx.lineWidth = 2;
    L.frames.forEach((f, i) => {
      const shot = shots[i];
      if (shot) ctx.drawImage(shot, f.x, f.y, f.w, f.h);
      ctx.strokeRect(f.x, f.y, f.w, f.h);
    });
    try {
      await Promise.all([
        document.fonts.load("40px 'Patrick Hand SC'"),
        document.fonts.load("24px 'DM Sans'"),
      ]);
    } catch {
      // the system's fonts will do
    }
    const now = new Date();
    ctx.fillStyle = ink;
    ctx.textAlign = "center";
    ctx.font = `${Math.round(L.footer * 0.36)}px 'Patrick Hand SC', cursive`;
    ctx.fillText("cozy cat booth", L.width / 2, L.footerY + L.footer * 0.52);
    ctx.font = `${Math.round(L.footer * 0.17)}px 'DM Sans', system-ui, sans-serif`;
    ctx.fillText(`${stripDate(now)} · cozycatkitchen.in`, L.width / 2, L.footerY + L.footer * 0.8);

    const blob = await new Promise((done) => c.toBlob(done, "image/png"));
    if (!(blob instanceof Blob) || mine !== session) return;
    // Issue 378: the wall shows a strip about 130 px wide, so it's pinned 300 px wide (about
    // 30 KB); the full strip stays on the phone.
    const small = document.createElement("canvas");
    small.width = PIN_WIDTH;
    small.height = Math.round((L.height * PIN_WIDTH) / L.width);
    const sctx = /** @type {CanvasRenderingContext2D} */ (small.getContext("2d"));
    sctx.imageSmoothingQuality = "high";
    sctx.drawImage(c, 0, 0, small.width, small.height);
    const jpeg = await new Promise((done) => small.toBlob(done, "image/jpeg", 0.85));
    stripJpeg = jpeg instanceof Blob ? jpeg : null;
    URL.revokeObjectURL(stripUrl);
    stripUrl = URL.createObjectURL(blob);
    stripFile = new File([blob], stripFileName(now), { type: "image/png" });
    printing.src = stripUrl;
    show("print");
    await sleep(PRINT_MS);
    if (mine !== session) return;
    stripImg.src = stripUrl;
    save.href = stripUrl;
    save.download = stripFile.name;
    share.hidden = !navigator.canShare?.({ files: [stripFile] });
    pinForm.hidden = !pinUrl || !stripJpeg;
    pinned.hidden = true;
    show("keep");
  }

  function reset() {
    session++;
    running = false;
    stopCamera();
    lockControls(false);
    setDots(0);
    countdown.hidden = true;
    faces = [];
    viewPoses = [];
    setHint();
    label.textContent = "ready when you are";
    shots = [];
    stripJpeg = null;
    pinForm.reset();
    pinForm.hidden = true;
    pinStatus.textContent = "";
    pinned.hidden = true;
  }

  function enter() {
    reset();
    show("snap");
    startCamera();
    loadFaceKit().then(setHint);
  }

  for (const open of document.querySelectorAll("[data-cck-booth-open]")) {
    open.addEventListener("click", () => {
      dialog.showModal();
      enter();
    });
  }
  for (const close of dialog.querySelectorAll("[data-cck-booth-close]"))
    close.addEventListener("click", () => dialog.close());
  dialog.addEventListener("close", () => {
    reset();
    URL.revokeObjectURL(stripUrl);
    stripUrl = "";
    stripFile = null;
  });
  $("[data-cck-booth-retry]").addEventListener("click", enter);
  $("[data-cck-booth-again]").addEventListener("click", enter);
  shutter.addEventListener("click", run);

  $("[data-cck-booth-looks]").addEventListener("click", (e) => {
    const b = /** @type {HTMLElement} */ (e.target).closest("[data-look]");
    if (!(b instanceof HTMLButtonElement) || b.disabled) return;
    look = /** @type {import("./cck-booth-core.js").Look} */ (b.dataset.look);
    for (const c of b.parentElement?.children ?? [])
      c.setAttribute("aria-pressed", String(c === b));
  });
  $("[data-cck-booth-stickers]").addEventListener("click", (e) => {
    const b = /** @type {HTMLElement} */ (e.target).closest("[data-sticker]");
    if (!(b instanceof HTMLButtonElement) || b.disabled) return;
    const kind = /** @type {import("./cck-booth-core.js").Sticker} */ (b.dataset.sticker);
    if (stickers.has(kind)) stickers.delete(kind);
    else stickers.add(kind);
    b.setAttribute("aria-pressed", String(stickers.has(kind)));
  });
  share.addEventListener("click", async () => {
    if (!stripFile) return;
    try {
      await navigator.share({ files: [stripFile], title: "Cozy Cat Booth" });
    } catch {
      // cancelled: nothing to do
    }
  });

  // Issue 373: pinned only with the cat's name and the tick, and only this strip.
  pinForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(pinForm);
    if (!String(data.get("name") ?? "").trim()) {
      pinStatus.textContent = "Your cat's name, please: it goes under the strip.";
      return;
    }
    if (data.get("consent") !== "yes") {
      pinStatus.textContent = "Tick the box to say it can go on the wall, please.";
      return;
    }
    if (!pinUrl || !stripJpeg) return;
    const button = /** @type {HTMLButtonElement} */ (pinForm.querySelector('[type="submit"]'));
    button.disabled = true;
    pinStatus.textContent = "Pinning…";
    data.set("photo", stripJpeg, "strip.jpg");
    try {
      const res = await fetch(pinUrl, { method: "POST", body: data });
      const r = await res.json().catch(() => ({}));
      if (!r.ok) {
        pinStatus.textContent = r.error ?? "That didn't go through. Try again, please.";
        return;
      }
      pinForm.hidden = true;
      pinned.hidden = false;
    } catch {
      pinStatus.textContent = "That didn't go through. Try again in a moment, please.";
    } finally {
      button.disabled = false;
    }
  });
}
